import type { ElaborateApi } from "./client";
import { createMockAdapter } from "./mockAdapter";
import { createRealAdapter } from "./realAdapter";

/**
 * Adapter selection. VITE_USE_MOCK switches between the in-memory fixtures and
 * the real `/api` backend. Default = REAL — a production build (no env set) MUST
 * ship the real adapter; the in-memory fixtures are opt-IN, selected ONLY when
 * VITE_USE_MOCK is explicitly truthy ("1"/"true"). This is the deploy-hygiene
 * invariant: a bundle built without the flag can never silently ship mocks.
 * (Local dev that wants the backend-free fixtures sets `VITE_USE_MOCK=1`.)
 *
 * STATIC FOLDING (load-bearing — do NOT reintroduce the optional chain).
 * `import.meta.env.VITE_USE_MOCK` is a build-time *literal substitution*; the
 * `?.` form defeats it, leaving `IS_MOCK` a runtime value so Rollup could not
 * fold the ternary below and dragged the entire fixture corpus — invented staff
 * at a real client domain, fabricated reviewer threads — into the shipped
 * production bundle. Written flat, the expression folds to `false` and the mock
 * branch is dead code. `web/vite.config.ts` additionally aliases
 * `./mockAdapter` to a fixture-free stub whenever the build is not mock, so the
 * corpus cannot ride in even if folding ever regresses; `bun run check:bundle`
 * fails the build if a sentinel survives.
 */
const MOCK_FLAG = import.meta.env.VITE_USE_MOCK;

export const IS_MOCK = MOCK_FLAG === "1" || MOCK_FLAG === "true";

// Deploy-hygiene beacon: expose the resolved adapter mode so a post-deploy
// browser smoke-check can assert `window.__ELA_ADAPTER__ === "real"` (and so the
// static `<meta name="ela-adapter">` in index.html has a runtime counterpart).
if (typeof window !== "undefined") {
  (window as unknown as { __ELA_ADAPTER__?: string }).__ELA_ADAPTER__ = IS_MOCK
    ? "mock"
    : "real";
}

export const api: ElaborateApi = IS_MOCK
  ? createMockAdapter()
  : createRealAdapter();

export type { ElaborateApi };
export * from "./types";
